import { BooleanFilterItemWithValues } from '@aboutyou/backbone/endpoints/filters/filters'
import { SALE_SLUG } from 'constants/filters'
import { exists } from 'helpers/exists'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { useObserver } from 'mobx-react-lite'
import React, { FC, useEffect, useMemo } from 'react'
import { PreferredFilterType } from 'services/providers/bapi/entities/filters/types'
import { useMainFilterTracking } from 'sharedComponents/ProductStream/filters/filterTracking'
import {
  useCategoryStore,
  useFilterStore,
} from 'store/sidecar/ProductStreamContext'
import styled, { css } from 'styled-components'
import Toggle from 'uiComponents/atoms/Toggle/Toggle'
import {
  bordersColor,
  darkGrayColor,
  primaryColor,
  saleAlternativeColor,
} from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsXS,
  fwMediumBold,
} from 'uiComponents/constants/typography'
import { BooleanSwitchGlobalStyle } from './styles'

interface FilterColorProps {
  filterColor: string | null
  height: number
  isActive?: boolean
}

const getColor = (filterSlug: PreferredFilterType) => {
  switch (filterSlug) {
    case SALE_SLUG:
      return saleAlternativeColor
    default:
      return null
  }
}

const useCategorizedFilter = (filterSlug: PreferredFilterType) => {
  const filterStore = useFilterStore()
  const categoryStore = useCategoryStore()
  const categoryIsSale = useObserver(() => categoryStore.isSale)

  if (filterSlug === SALE_SLUG && categoryIsSale) {
    return undefined
  }

  return filterStore.availableFilters.find(
    ({ slug }) => slug === filterSlug,
  ) as BooleanFilterItemWithValues | undefined
}

const useBooleanFilterProps = (filterSlug: PreferredFilterType) => {
  const filterStore = useFilterStore()
  const filter = useCategorizedFilter(filterSlug)

  return useMemo(
    () => ({
      isAvailable: exists(filter),
      isSelected: Boolean(filterStore.selectedFilters[filterSlug]?.length),
      toggle: () => filterStore.toggleSelected(filterSlug),
      filter,
    }),
    [filter, filterSlug, filterStore],
  )
}

const BooleanFilterWrapper = styled.div<{ inline?: boolean }>`
  ${({ inline }) =>
    inline
      ? css`
          display: inline-block;
        `
      : css`
          display: flex;
          flex: 1;
          justify-content: space-between;
        `}
`

const Label = styled.label<FilterColorProps>`
  ${fontFamilyPrimary};
  ${fwMediumBold};
  font-size: ${fsXS}px;
  color: ${props => (props.isActive ? primaryColor : darkGrayColor)};
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
  padding: 0 8px;
  height: ${props => props.height}px;
  border-radius: 2px;
  border: solid 1px ${props => props.filterColor || bordersColor};
  flex-shrink: 0;
`

const StyledToggle = styled(Toggle)`
  margin-right: 10px;
  color: #9b9b9b;
  gap: 8px;
`

const BooleanFilter: FC<{
  filter: BooleanFilterItemWithValues
}> = ({ filter }) => {
  const filterProps = useBooleanFilterProps(filter.slug as PreferredFilterType)
  const trackFilterChanges = useMainFilterTracking()
  const { t } = useTranslation()
  const [selected, setSelected] = React.useState(filterProps.isSelected)

  useEffect(() => {
    setSelected(filterProps.isSelected)
  }, [filterProps.isSelected])

  const onChange = () => {
    setSelected(!selected)
    filterProps.toggle()
    if (filterProps.filter) {
      trackFilterChanges(filterProps.filter.slug)
    }
  }

  if (!filterProps.isAvailable) {
    return null
  }

  if (filterProps?.filter?.values) {
    const matchingFilter = filterProps.filter.values
      .filter(Boolean)
      .find(filter => filter.name && filter.productCount > 0)

    if (!matchingFilter) {
      return null
    }
  }

  return (
    <BooleanFilterWrapper className="horizontal-boolean-filter" inline>
      <BooleanSwitchGlobalStyle />
      <Label
        data-testid={`Toggle-${filter.slug}`}
        filterColor={getColor(filter.slug as PreferredFilterType)}
        height={32}
        isActive={selected}
      >
        <StyledToggle
          label={t(filter.name)}
          checked={selected}
          onChange={onChange}
        />
      </Label>
    </BooleanFilterWrapper>
  )
}

export default BooleanFilter
